import { cloneElement, ReactElement, ReactNode } from 'react';

import { css } from '@compiled/react';

import { media } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { ButtonProps } from '@/libraries/design/components/button/src/button';
import { wacToken, ColumnLayout } from '@/libraries/design/foundations';

const callToActionStyles = css({
	maxWidth: '1440px',
	marginInline: 'auto',
	textAlign: 'center',
});

const typographyOverrides = css({
	marginBlock: `${token('space.0')} !important`,
});

const polygonStyles = css({
	background: 'var(--background-color)',
	clipPath: 'polygon(1.5% 0, 70% 3%, 70% 13%, 97% 9%, 97.5% 89%, 40% 100%, 39.9% 90%, 1% 88%)',
	[media.below.sm]: {
		clipPath: 'polygon(0 0, 100% 4%, 100% 97%, 28% 100%, 28% 87%, 0 87%)',
	},
	padding: `${token('space.1000')} 0`,
});

const contentStyles = css({
	display: 'grid',
	gap: token('space.100'),
	marginBlockEnd: token('space.400'),
});

export type CallToActionButtonProps = {
	text: string;
} & ButtonProps;

export type CallToActionBackgroundColors = 'black' | 'blue';

export type CallToActionProps = {
	header: ReactNode | string;
	paragraph?: ReactNode | string;
	button: ReactElement<ButtonProps> | ReactNode;
	backgroundColor?: CallToActionBackgroundColors;
};

export const CallToAction = ({
	header,
	paragraph,
	button,
	backgroundColor = 'black',
}: CallToActionProps) => {
	const buttonProps =
		backgroundColor === 'blue'
			? {
					variant: 'primary_alt' as const,
					colorAgainst: 'other' as const,
				}
			: { variant: 'primary' as const, colorAgainst: 'black' as const };

	const transformedButton = cloneElement(button as ReactElement<ButtonProps>, buttonProps);

	return (
		<section css={callToActionStyles}>
			<div
				style={
					{
						'--background-color': wacToken(`color.primary.${backgroundColor}`),
					} as React.CSSProperties
				}
				css={polygonStyles}
			>
				<ColumnLayout layout="8">
					<div css={contentStyles}>
						<h2
							className="h3"
							css={typographyOverrides}
							style={{ color: wacToken('color.neutral.light0') }}
						>
							{header}
						</h2>
						{paragraph && (
							<p
								className=".body-md"
								css={typographyOverrides}
								style={{
									color: wacToken('color.neutral.light0'),
								}}
							>
								{paragraph}
							</p>
						)}
					</div>
				</ColumnLayout>
				{transformedButton}
			</div>
		</section>
	);
};
